import type { RealtimeChannel } from '@supabase/supabase-js'
import { useQueryCache } from '@pinia/colada'

export function useRequestChatGlobalSubscription () {
  const route = useRoute()

  const authStore = useAuthStore()
  const queryCache = useQueryCache()

  // Prefetch the chats list globally so the nav-tab unread badge populates
  // on app load regardless of which page the user lands on.
  const { refetch: refetchChatsList } = useRequestChatsQuery({
    enabled: () => !!authStore.user
  })
  const debouncedRefetchChatsList = radashDebounce({ delay: 600 }, refetchChatsList)

  let channel: TNullable<RealtimeChannel> = null

  function mapRealtimeRow (row: Record<string, string>): TRequestChatMessage {
    return {
      id: row.id!,
      chatSessionId: row.chat_session_id!,
      senderUserId: row.sender_user_id!,
      content: row.content!,
      createdAt: helpers.normalizeTimestampToUtc(row.created_at!)
    }
  }

  function subscribe () {
    if (channel) {
      return
    }

    channel = supabase
      .channel('request_chat_messages:global')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'request_chat_messages'
        },
        (payload) => {
          const row = payload.new as Record<string, string>
          const message = mapRealtimeRow(row)
          const isOwnMessage = message.senderUserId === authStore.user?.id

          const detailKey = requestChatQueryKeys.chat(message.chatSessionId)
          const currentDetail = queryCache.getQueryData<TRequestChatDetail>(detailKey)
          const isMessageExists = currentDetail?.messages.some(m => m.id === message.id)

          if (currentDetail && !isMessageExists) {
            queryCache.setQueryData(detailKey, {
              ...currentDetail,
              messages: [...currentDetail.messages, message]
            })
          }

          if (isOwnMessage) {
            return
          }

          const isViewingChatPage = route.name === routeNames.requestChatDetail &&
            route.params.chatId === message.chatSessionId

          // Refetch chats list to update last messages and unread count
          if (!isViewingChatPage) {
            debouncedRefetchChatsList()
          }
        }
      )
      .subscribe()
  }

  function unsubscribe () {
    if (channel) {
      supabase.removeChannel(channel)
      channel = null
    }
  }

  watch(() => authStore.user, (user) => {
    if (user) {
      subscribe()
    } else {
      unsubscribe()
    }
  }, { immediate: true })

  onUnmounted(() => {
    unsubscribe()
  })

  return { subscribe, unsubscribe }
}
